import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/config';
import { uploadFile } from '../firebase/r2storage';
import { useParams } from 'react-router-dom';
import { Video, StopCircle, Send, Clock, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

export default function InterviewInterface() {
  const { interviewId, candidateId } = useParams();
  const [interview, setInterview] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [interviewCompleted, setInterviewCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allAnswers, setAllAnswers] = useState([]);
  const [hasRecorded, setHasRecorded] = useState(false);

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    fetchInterviewData();
    return () => {
      stopRecording();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    // Ensure video plays when stream is available
    if (videoRef.current && streamRef.current && interviewStarted) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(err => {
        console.error('Error playing video:', err);
      });
    }
  }, [interviewStarted, streamRef.current]);

  useEffect(() => {
    if (isRecording && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmitAnswer();
            return 300;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [isRecording, timeRemaining]);

  const fetchInterviewData = async () => {
    try {
      const docRef = doc(db, 'interviews', interviewId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const interviewData = { id: docSnap.id, ...docSnap.data() };
        setInterview(interviewData);
        
        const candidateData = interviewData.candidates?.find(c => c.id === candidateId);
        if (candidateData) {
          setCandidate(candidateData);
        }
      }
    } catch (error) {
      console.error('Error fetching interview:', error);
      toast.error('Failed to load interview');
    }
  };

  const startInterview = async () => {
    try {
      console.log('Requesting camera and microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }, 
        audio: true 
      });
      
      console.log('Media stream obtained:', stream);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Ensure video plays
        try {
          await videoRef.current.play();
          console.log('Video playing');
        } catch (playError) {
          console.error('Error playing video:', playError);
        }
      }

      setInterviewStarted(true);
      generateNextQuestion();
      toast.success('Interview started! Good luck!');
    } catch (error) {
      console.error('Error accessing media devices:', error);
      let errorMessage = 'Please allow camera and microphone access';
      
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Camera/microphone access denied. Please allow access and refresh the page.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No camera or microphone found. Please connect a device.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use by another application.';
      }
      
      toast.error(errorMessage);
    }
  };

  const generateNextQuestion = async () => {
    setLoading(true);
    try {
      // In production, this would call your AI service
      // For now, we'll generate questions based on the job requirements
      const questions = generateQuestionsFromRequirements();
      
      if (questionIndex < questions.length) {
        setCurrentQuestion(questions[questionIndex]);
      } else {
        completeInterview();
      }
    } catch (error) {
      console.error('Error generating question:', error);
      toast.error('Failed to generate question');
    } finally {
      setLoading(false);
    }
  };

  const generateQuestionsFromRequirements = () => {
    if (!interview) return [];
    
    // Use custom questions if available, otherwise use default questions
    if (interview.questions && interview.questions.length > 0) {
      return interview.questions;
    }
    
    const baseQuestions = [
      `Tell me about yourself and your experience relevant to the ${interview.positionTitle} position.`,
      `What interests you about this ${interview.positionTitle} role at our company?`,
      `Can you describe your experience with ${interview.requiredSkills?.[0] || 'the required technologies'}?`,
      `Tell me about a challenging project you worked on and how you overcame obstacles.`,
      `How do you stay updated with the latest trends in ${interview.department}?`,
      `Describe a situation where you had to work in a team. What was your role?`,
      `What are your salary expectations for this role?`,
      `Where do you see yourself in 5 years?`
    ];

    return baseQuestions;
  };

  const startRecording = async () => {
    try {
      recordedChunksRef.current = [];
      
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: 'video/webm;codecs=vp8,opus'
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setHasRecorded(false);
      setTimeRemaining(300);
      toast.success('Recording started!');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setHasRecorded(true);
      clearInterval(timerRef.current);
      toast.success('Recording stopped! You can review or submit your answer.');
    }
  };

  const handleSubmitAnswer = async () => {
    if (recordedChunksRef.current.length === 0) {
      toast.error('Please record your answer before submitting');
      return;
    }

    if (isRecording) {
      stopRecording();
      // Wait a bit for recording to fully stop
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setLoading(true);

    try {
      console.log('Uploading video...');
      console.log('Interview ID:', interviewId);
      console.log('Candidate ID:', candidateId);
      console.log('Question Index:', questionIndex);
      
      // Upload video
      const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
      console.log('Blob size:', blob.size, 'bytes');
      
      if (blob.size === 0) {
        throw new Error('Recording is empty. Please try recording again.');
      }
      
      const timestamp = Date.now();
      const fileName = `q${questionIndex}_${timestamp}.webm`;
      const storagePath = `interviews/${interviewId}/${candidateId}/${fileName}`;
      
      console.log('Storage path:', storagePath);
      console.log('Uploading to R2 storage...');
      
      // Convert blob to File for R2 upload
      const file = new File([blob], fileName, { type: 'video/webm' });
      const uploadResult = await uploadFile(file, storagePath);
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Upload failed');
      }
      
      console.log('Upload complete:', uploadResult);
      const videoUrl = uploadResult.url;
      console.log('Video URL obtained:', videoUrl);

      const answerData = {
        questionIndex,
        question: currentQuestion,
        videoUrl,
        timestamp: new Date().toISOString(),
        timeSpent: 300 - timeRemaining
      };

      const newAllAnswers = [...allAnswers, answerData];
      setAllAnswers(newAllAnswers);
      
      // Reset for next question
      recordedChunksRef.current = [];
      setHasRecorded(false);
      setTimeRemaining(300);
      
      const questions = generateQuestionsFromRequirements();
      if (questionIndex + 1 < questions.length) {
        setQuestionIndex(prev => prev + 1);
        setCurrentQuestion(questions[questionIndex + 1]);
        toast.success('Answer submitted! Next question...');
      } else {
        await completeInterview(answerData);
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Full error:', JSON.stringify(error, null, 2));
      
      let errorMessage = 'Failed to submit answer';
      if (error.code === 'storage/unauthorized') {
        errorMessage = 'Storage permission denied. Please check your permissions.';
        toast.error(errorMessage);
        console.error('Please check Firebase Functions permissions');
      } else if (error.code === 'storage/canceled') {
        errorMessage = 'Upload was canceled. Please try again.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const completeInterview = async (lastAnswer = null) => {
    setLoading(true);
    try {
      const finalAnswers = lastAnswer ? [...allAnswers, lastAnswer] : allAnswers;
      
      console.log('Completing interview with', finalAnswers.length, 'answers');
      
      // Save all answers to Firestore
      const interviewRef = doc(db, 'interviews', interviewId);
      const interviewDoc = await getDoc(interviewRef);
      
      if (!interviewDoc.exists()) {
        throw new Error('Interview not found');
      }
      
      const interviewData = interviewDoc.data();
      console.log('Current interview data:', interviewData);
      
      const updatedCandidates = interviewData.candidates.map(c => {
        if (c.id === candidateId) {
          return {
            ...c,
            status: 'completed',
            answers: finalAnswers,
            completedAt: new Date().toISOString(),
            overallRating: null // Will be set by AI analysis
          };
        }
        return c;
      });

      console.log('Updating candidates array...');
      await updateDoc(interviewRef, {
        candidates: updatedCandidates
      });

      console.log('Interview completed successfully');

      // Stop camera
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      setInterviewCompleted(true);
      toast.success('Interview completed! Thank you for your time.');
    } catch (error) {
      console.error('Error completing interview:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Failed to complete interview';
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please contact support.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!interview || !candidate) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (interviewCompleted) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Interview Completed!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Thank you for completing the interview for {interview.positionTitle}.
            Our team will review your responses and get back to you soon.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-900 dark:text-blue-300">
              Your responses are being analyzed by our AI system. You will receive a detailed
              feedback report via email within 24-48 hours.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!interviewStarted) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Welcome, {candidate.name}!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You're about to start your AI-powered interview for the position of{' '}
            <span className="font-semibold">{interview.positionTitle}</span>.
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <Video className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">Video Recording</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Your camera will be enabled throughout the interview
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">Time Limit</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  You have 5 minutes maximum per question
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-blue-600 mt-1" />
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">Video Only</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Answer all questions via video recording
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={startInterview}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <Video className="w-5 h-5" />
            Start Interview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 text-white p-4 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">{interview.positionTitle}</h2>
              <p className="text-blue-100 text-sm">Question {questionIndex + 1} of {generateQuestionsFromRequirements().length}</p>
            </div>
            {isRecording && (
              <div className="flex items-center gap-2 bg-red-500 px-4 py-2 rounded-lg animate-pulse">
                <div className="w-3 h-3 bg-white rounded-full"></div>
                <span className="font-mono font-bold">{formatTime(timeRemaining)}</span>
              </div>
            )}
          </div>

          <div className="p-6">
            {/* Question */}
            <div className="mb-6 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-600">
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                Question {questionIndex + 1}:
              </h3>
              <p className="text-lg text-gray-900 dark:text-white leading-relaxed">
                {currentQuestion}
              </p>
            </div>

            {/* Video Preview - Large and Centered */}
            <div className="mb-6">
              <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-2xl" style={{ aspectRatio: '16/9', maxHeight: '500px' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
                {isRecording && (
                  <div className="absolute top-4 right-4 flex items-center gap-2 bg-red-600 px-4 py-2 rounded-full animate-pulse">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                    <span className="text-white font-bold">REC</span>
                  </div>
                )}
                {!isRecording && hasRecorded && (
                  <div className="absolute top-4 right-4 flex items-center gap-2 bg-green-600 px-4 py-2 rounded-full">
                    <CheckCircle className="w-4 h-4 text-white" />
                    <span className="text-white font-medium">Recorded</span>
                  </div>
                )}
                {!streamRef.current && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                    <div className="text-center text-white">
                      <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg">Camera will appear here</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Instructions */}
            {!isRecording && !hasRecorded && (
              <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  💡 <strong>Tip:</strong> Click "Start Recording" to begin your video answer. You have up to 5 minutes per question.
                </p>
              </div>
            )}

            {/* Recording Controls */}
            <div className="flex gap-4">
              {!isRecording && !hasRecorded && (
                <button
                  onClick={startRecording}
                  className="flex-1 px-6 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-3 text-lg font-semibold shadow-lg"
                >
                  <Video className="w-6 h-6" />
                  Start Recording
                </button>
              )}

              {isRecording && (
                <button
                  onClick={stopRecording}
                  className="flex-1 px-6 py-4 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-3 text-lg font-semibold shadow-lg"
                >
                  <StopCircle className="w-6 h-6" />
                  Stop Recording
                </button>
              )}

              {!isRecording && hasRecorded && (
                <>
                  <button
                    onClick={startRecording}
                    className="flex-1 px-6 py-4 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center justify-center gap-3 text-lg font-semibold shadow-lg"
                  >
                    <Video className="w-6 h-6" />
                    Re-record Answer
                  </button>
                  <button
                    onClick={handleSubmitAnswer}
                    disabled={loading}
                    className="flex-1 px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-lg font-semibold shadow-lg"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Send className="w-6 h-6" />
                        Submit Answer
                      </>
                    )}
                  </button>
                </>
              )}
            </div>

            {/* Progress Info */}
            <div className="mt-6 flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
              <span>Time limit: 5 minutes per question</span>
              <span>Questions remaining: {generateQuestionsFromRequirements().length - questionIndex - 1}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
